import { apiClient } from "./apiClient";
import type {
  PreferencesResponse,
  PreferencesUpdateRequest,
  PreferenceHistoryEntry,
  RollbackRequest,
  GenerationContextResponse,
  FeedbackPattern,
} from "../types/tenantPreferences";
import type {
  AnalyzePreferencesRequest,
  PreferenceDiscoveryResponse,
} from "../types/preferenceDiscovery";

const API_BASE = "/tenant-preferences";

export const tenantPreferencesApi = {
  async getPreferences(
    website: string,
    category?: string,
  ): Promise<PreferencesResponse> {
    const params: Record<string, string> = { website };
    if (category) params.category = category;
    const response = await apiClient.get<PreferencesResponse>(API_BASE, {
      params,
    });
    return response.data;
  },

  async updatePreferences(
    website: string,
    body: PreferencesUpdateRequest,
  ): Promise<PreferencesResponse> {
    const response = await apiClient.put<PreferencesResponse>(API_BASE, body, {
      params: { website },
    });
    return response.data;
  },

  async getHistory(website: string): Promise<PreferenceHistoryEntry[]> {
    const response = await apiClient.get<
      PreferenceHistoryEntry[] | { history: PreferenceHistoryEntry[] }
    >(`${API_BASE}/history`, { params: { website } });
    // Handle both raw array (backend) and wrapped object (mock)
    const data = response.data;
    return Array.isArray(data) ? data : data.history;
  },

  async rollback(
    website: string,
    body: RollbackRequest,
  ): Promise<PreferencesResponse> {
    const response = await apiClient.post<PreferencesResponse>(
      `${API_BASE}/rollback`,
      body,
      { params: { website } },
    );
    return response.data;
  },

  async listPatterns(website: string): Promise<FeedbackPattern[]> {
    const response = await apiClient.get<FeedbackPattern[]>(
      "/content-feedback/patterns",
      { params: { tenant_id: website } },
    );
    return response.data;
  },

  async togglePattern(
    website: string,
    patternId: string,
    isActive: boolean,
  ): Promise<FeedbackPattern> {
    const response = await apiClient.patch<FeedbackPattern>(
      `/content-feedback/patterns/${patternId}`,
      { is_active: isActive },
      { params: { tenant_id: website } },
    );
    return response.data;
  },

  async getGenerationContext(
    website: string,
  ): Promise<GenerationContextResponse> {
    const response = await apiClient.get<GenerationContextResponse>(
      `${API_BASE}/generation-context`,
      { params: { website } },
    );
    return response.data;
  },

  async analyzePreferences(
    website: string,
    body: AnalyzePreferencesRequest = {},
  ): Promise<PreferenceDiscoveryResponse> {
    const response = await apiClient.post<PreferenceDiscoveryResponse>(
      `${API_BASE}/analyze`,
      body,
      { params: { website } },
    );
    return response.data;
  },
};
